import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, BadgeList } from '../../components/Badge';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { FilterChips } from '../../components/FilterChips';
import { IconPlus } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { useLocale } from '../../lib/locale';
import { usePageTitle } from '../../lib/page';
import { useRecipes } from './api';
import { recipesDict } from './i18n';
import { RecipeMeta } from './RecipeMeta';
import { filterRecipes, tagsInUse } from './search';

/**
 * Every recipe, A–Z, narrowed as the user types or picks tags. The whole
 * collection is already loaded, so filtering never asks the server.
 */
export function RecipeList() {
  const t = useDict(recipesDict);
  usePageTitle(t.titleSuffix);
  const { locale } = useLocale();
  const formId = useId();
  const { data: recipes } = useRecipes();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);

  const collator = useMemo(() => new Intl.Collator(locale), [locale]);
  const sorted = useMemo(
    () => [...(recipes ?? [])].sort((a, b) => collator.compare(a.name, b.name)),
    [recipes, collator],
  );
  const tags = useMemo(() => tagsInUse(sorted, collator), [sorted, collator]);
  // A tag that left the collection (its last recipe edited) stops filtering.
  const active = useMemo(
    () => picked.filter((tag) => tags.includes(tag)),
    [picked, tags],
  );
  const shown = useMemo(
    () => filterRecipes(sorted, search, active),
    [sorted, search, active],
  );

  const header = (
    <PageHeader
      title={t.recipes}
      actions={
        <Link
          to="/recipes/new"
          className="btn-primary-icon"
          aria-label={t.newRecipe}
          title={t.newRecipe}
        >
          <IconPlus />
        </Link>
      }
    />
  );

  if (!recipes) return header;

  if (recipes.length === 0)
    return (
      <>
        {header}
        <EmptyState size="lg">
          {t.empty}{' '}
          <Link to="/recipes/new" className="text-link">
            {t.addFirst}
          </Link>
        </EmptyState>
      </>
    );

  return (
    <>
      {header}
      <Panel>
        <div className="form-stack">
          <Field
            id={`${formId}-search`}
            label={t.search}
            type="search"
            autoComplete="off"
            placeholder={t.searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {tags.length > 0 && (
            <FilterChips
              label={t.tags}
              options={tags}
              selected={active}
              onChange={setPicked}
            />
          )}
        </div>
      </Panel>

      {shown.length > 0 ? (
        <Panel className="recipe-list-card">
          <ul className="recipe-list">
            {shown.map((recipe) => (
              <li key={recipe.id}>
                <Link to={`/recipes/${recipe.id}`} className="recipe-row">
                  <span className="recipe-row-name">{recipe.name}</span>
                  <RecipeMeta recipe={recipe} />
                  {recipe.tags.length > 0 && (
                    <BadgeList>
                      {recipe.tags.map((tag) => (
                        <Badge key={tag} tone="neutral">
                          {tag}
                        </Badge>
                      ))}
                    </BadgeList>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      ) : (
        <EmptyState size="lg">{t.noMatch}</EmptyState>
      )}
    </>
  );
}
